import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminBenefitTable() {
  const [tables, setTables] = useState(null);
  const [rows, setRows] = useState([]);
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/benefit-tables");
      setTables(data);
      const active = data.find((t) => t.active);
      if (active) setRows(active.rows.map((r) => ({ ...r })));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setRow = (i, k, v) => { const r = [...rows]; r[i] = { ...r[i], [k]: k === "category" ? v : (v === "" ? "" : Number(v)) }; setRows(r); };
  const addRow = () => setRows([...rows, { category: "Familia", family_size: 4, income_from: 0, income_to: 0, percentage: 0 }]);
  const rmRow = (i) => setRows(rows.filter((_, x) => x !== i));

  const CATS = ["Familia IAJAD", "Familia", "Pareja", "Individual", "Estudiante", "Senior", "Senior Pareja", "Olami", "Comunitario"];

  const saveNew = async () => {
    if (!version.trim()) { toast.error("Ingresa un nombre de versión."); return; }
    setBusy(true);
    try {
      const clean = rows.map((r) => {
        const row = { category: r.category || null, income_from: Number(r.income_from), income_to: Number(r.income_to), percentage: Number(r.percentage) };
        if (r.family_size !== "" && r.family_size != null) row.family_size = Number(r.family_size);
        return row;
      });
      await api.post("/admin/benefit-tables", { version, rows: clean, note: "Actualización desde panel" });
      toast.success("Nueva versión creada y activada.");
      setVersion(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const activate = async (tid) => {
    try { await api.post(`/admin/benefit-tables/${tid}/activate`); toast.success("Versión activada."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (tables === null) return <AdminLayout><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold mb-1">Tabla de becas</h1>
      <p className="text-muted-foreground mb-6">Define, por <strong>categoría</strong> y rango de ingreso familiar ajustado, el porcentaje de beca. Para Familia / Familia IAJAD el % varía según el N° de integrantes. Cada cambio crea una nueva versión.</p>

      <div className="bg-white rounded-2xl border border-border p-6 mb-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-500">
            <th className="py-2 pr-3">Categoría</th><th className="py-2 pr-3">N° integrantes</th><th className="py-2 pr-3">Ingreso desde</th><th className="py-2 pr-3">Ingreso hasta</th><th className="py-2 pr-3">Beca %</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="py-2 pr-3">
                  <select className="h-10 rounded-lg border border-border px-2 text-sm bg-white" value={r.category || ""} onChange={(e) => setRow(i, "category", e.target.value)} data-testid={`bt-cat-${i}`}>
                    <option value="">—</option>
                    {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="py-2 pr-3"><Input type="number" className="h-10 w-20 rounded-lg" value={r.family_size ?? ""} placeholder="—" onChange={(e) => setRow(i, "family_size", e.target.value)} /></td>
                <td className="py-2 pr-3"><Input type="number" className="h-10 w-32 rounded-lg" value={r.income_from} onChange={(e) => setRow(i, "income_from", e.target.value)} /></td>
                <td className="py-2 pr-3"><Input type="number" className="h-10 w-32 rounded-lg" value={r.income_to} onChange={(e) => setRow(i, "income_to", e.target.value)} /></td>
                <td className="py-2 pr-3"><Input type="number" className="h-10 w-20 rounded-lg" value={r.percentage} onChange={(e) => setRow(i, "percentage", e.target.value)} /></td>
                <td><button onClick={() => rmRow(i)} className="text-destructive"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-3">Deja "N° integrantes" vacío para categorías sin distinción de tamaño (Individual, Pareja, Senior, etc.).</p>
        <Button variant="outline" onClick={addRow} className="rounded-xl border-2 mt-4"><Plus className="w-4 h-4 mr-1" /> Agregar fila</Button>
      </div>

      <div className="bg-white rounded-2xl border border-border p-6 mb-6 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-semibold uppercase text-slate-500">Nombre de la nueva versión</Label>
          <Input className="h-12 rounded-xl mt-1.5" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Ej: 2027-v2" data-testid="table-version-input" />
        </div>
        <Button onClick={saveNew} disabled={busy} className="btn-lift h-12 rounded-xl px-8" data-testid="btn-save-table">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Guardar como nueva versión"}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border p-6">
        <h2 className="text-lg font-semibold mb-4">Versiones</h2>
        <ul className="space-y-2">
          {tables.map((t) => (
            <li key={t.id} className="flex items-center justify-between border border-border rounded-xl p-3">
              <div><span className="font-semibold">{t.version}</span> {t.is_demo && <span className="text-xs text-amber-700 ml-2">DEMO</span>}
                <span className="text-xs text-muted-foreground ml-2">{new Date(t.created_at).toLocaleDateString("es-CL")}</span></div>
              {t.active ? <span className="text-emerald-700 text-sm font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Activa</span>
                : <Button size="sm" variant="outline" className="rounded-lg" onClick={() => activate(t.id)}>Activar</Button>}
            </li>
          ))}
        </ul>
      </div>
    </AdminLayout>
  );
}
